import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { PORTALS } from "@/lib/portal";
import { listPermissions } from "@/services/api/permissions";
import { listGroups } from "@/services/api/groups";
import { listUsers } from "@/services/api/users";
import type { Permission } from "@/services/api/types";

/** Permission matrix: which group/user holds which permission (inherited vs direct). */
export function PermissionsPage() {
  const { t } = useTranslation();
  const permissions = useQuery({
    queryKey: ["permissions", "all"],
    queryFn: () => listPermissions({ limit: 500 }),
  });
  const groups = useQuery({
    queryKey: ["groups", "all"],
    queryFn: () => listGroups({ limit: 500 }),
  });
  const users = useQuery({
    queryKey: ["users", "all"],
    queryFn: () => listUsers({ limit: 500 }),
  });

  const perms: Permission[] = permissions.data?.items ?? [];
  const allGroups = groups.data?.items ?? [];
  const allUsers = users.data?.items ?? [];

  const permissionLabel = (code: string) => code;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">{t("nav.permissions")}</h1>
        <p className="text-sm text-muted-foreground">
          {t("admin.inherited")} <span className="text-green-600">✓</span> · {t("admin.direct")}{" "}
          <span className="text-red-600">✗</span>
        </p>
      </div>

      <Tabs defaultValue="groups">
        <TabsList>
          <TabsTrigger value="groups">{t("admin.groups")}</TabsTrigger>
          <TabsTrigger value="users">{t("admin.users")}</TabsTrigger>
        </TabsList>

        <TabsContent value="groups">
          <div className="space-y-6">
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="sticky left-0 bg-muted/50 px-3 py-2 text-left font-medium">
                      {t("admin.permissions")}
                    </th>
                    {allGroups.map((group) => (
                      <th key={group.id} className="px-3 py-2 text-left font-medium">
                        {group.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {perms.map((permission) => (
                    <tr key={permission.id} className="border-b last:border-0">
                      <td className="sticky left-0 bg-background px-3 py-1.5 font-mono text-xs">
                        {permissionLabel(permission.code)}
                      </td>
                      {allGroups.map((group) => (
                        <td key={group.id} className="px-3 py-1.5 text-center">
                          <Checkbox
                            checked={(group.permission_ids ?? []).includes(permission.id)}
                            disabled
                            aria-label={`${group.name} — ${permission.code}`}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div>
              <h2 className="mb-2 text-lg font-semibold">{t("admin.portal")}</h2>
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="px-3 py-2 text-left font-medium">{t("admin.portal")}</th>
                      {allGroups.map((group) => (
                        <th key={group.id} className="px-3 py-2 text-left font-medium">
                          {group.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {PORTALS.map((portal) => (
                      <tr key={portal.key} className="border-b last:border-0">
                        <td className="px-3 py-1.5 font-medium">{t(portal.titleKey)}</td>
                        {allGroups.map((group) => {
                          const hasAccess = portal.permissions.some((code) =>
                            (group.permission_ids ?? []).some((pid) =>
                              perms.some((p) => p.id === pid && p.code === code),
                            ),
                          );
                          return (
                            <td key={group.id} className="px-3 py-1.5 text-center">
                              {hasAccess ? "✓" : "—"}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="users">
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="sticky left-0 bg-muted/50 px-3 py-2 text-left font-medium">
                    {t("admin.user")}
                  </th>
                  {perms.map((permission) => (
                    <th
                      key={permission.id}
                      className="px-2 py-2 text-center font-mono text-[10px] font-normal"
                    >
                      {permission.code}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {allUsers.map((user) => {
                  const groupPermIds = new Set<number>();
                  for (const group of allGroups) {
                    if ((user.group_ids ?? []).includes(group.id)) {
                      (group.permission_ids ?? []).forEach((pid) => groupPermIds.add(pid));
                    }
                  }
                  const direct = new Set(user.grant_permission_ids ?? []);
                  return (
                    <tr key={user.id} className="border-b last:border-0">
                      <td className="sticky left-0 bg-background px-3 py-1.5">
                        {user.email}
                      </td>
                      {perms.map((permission) => {
                        const inherited = groupPermIds.has(permission.id);
                        const directGrant = direct.has(permission.id);
                        return (
                          <td key={permission.id} className="px-2 py-1.5 text-center">
                            {directGrant ? (
                              <span className="text-red-600" title={t("admin.direct")}>
                                ✗
                              </span>
                            ) : inherited ? (
                              <span className="text-green-600" title={t("admin.inherited")}>
                                ✓
                              </span>
                            ) : (
                              <span className="text-muted-foreground/30">·</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
